// Copyright 2025, University of Maryland, All Rights Reserved
import { useMemo, type FC } from 'react'
import { ChevronDown } from 'lucide-react'

import { Label, Input } from '@/components/ui'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAgentDatasets, useDatasets } from '@/subsystems/pipelines'

import type { DatasourceConfigProps, AnalysisResultsDatasourceSettings } from '../types'

/**
 * Configuration form for AnalysisResults datasource.
 * Fetches existing analysis outputs from DynamoDB/S3 for downstream nodes.
 */
export const AnalysisResultsConfig: FC<DatasourceConfigProps<AnalysisResultsDatasourceSettings>> = ({
  config,
  onChange,
  orgId,
  workspaceId,
  agentId,
}) => {
  const upstreamDatasetKeys = config?.upstreamDatasetKeys ?? []
  const sourceOrgId = config?.sourceOrgId ?? ''
  const sourceWsId = config?.sourceWsId ?? ''

  // The owning agent's datasets; outside an agent pipeline, fall back to every
  // agent's datasets in the workspace (the workspace hook is disabled otherwise).
  const workspaceDatasets = useDatasets(agentId ? undefined : orgId, workspaceId)
  const agentDatasets = useAgentDatasets(orgId, workspaceId, agentId)
  const { datasets, loading } = agentId ? agentDatasets : workspaceDatasets

  const selected = useMemo(() => new Set(upstreamDatasetKeys), [upstreamDatasetKeys])
  const isSelected = (d: { key: string }) => selected.has(d.key)

  const toggle = (datasetKey: string, checked: boolean) => {
    const next = new Set(selected)
    if (checked) next.add(datasetKey)
    else next.delete(datasetKey)
    // Preserve dataset declaration order rather than user-click order.
    const ordered: string[] = []
    for (const d of datasets) {
      if (next.has(d.key) && !ordered.includes(d.key)) ordered.push(d.key)
    }
    // Include any selected keys we didn't recognize (cross-workspace, stale, etc.),
    // so the form doesn't silently drop them.
    for (const id of next) if (!ordered.includes(id)) ordered.push(id)
    onChange('upstreamDatasetKeys', ordered)
  }

  const label =
    upstreamDatasetKeys.length === 0
      ? 'Select datasets…'
      : upstreamDatasetKeys.length === 1
        ? upstreamDatasetKeys[0]
        : `${upstreamDatasetKeys.length} datasets selected`

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="upstream-dataset-keys">Upstream Dataset Keys</Label>
        <p className="text-xs text-muted-foreground">
          Datasets produced by upstream analysis nodes that this node should consume.
        </p>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              id="upstream-dataset-keys"
              type="button"
              variant="outline"
              className="w-full max-w-sm justify-between font-normal"
              disabled={loading}
            >
              <span className="truncate">{loading ? 'Loading…' : label}</span>
              <ChevronDown className="h-4 w-4 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] max-h-72 overflow-y-auto">
            {datasets.length === 0 && !loading && (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                No datasets in this workspace.
              </div>
            )}
            {datasets.map(d => (
              <DropdownMenuCheckboxItem
                key={d.key}
                checked={isSelected(d)}
                onCheckedChange={c => toggle(d.key, Boolean(c))}
                onSelect={e => e.preventDefault()}
              >
                <span className="flex flex-col">
                  <span>{d.label || d.key}</span>
                  {d.label && d.label !== d.key && (
                    <span className="text-xs text-muted-foreground">{d.key}</span>
                  )}
                </span>
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {/* Show unresolved keys (selected but not present in the dataset list) for visibility. */}
        {upstreamDatasetKeys.some(k => !datasets.find(d => d.key === k)) && (
          <p className="text-xs text-amber-600">
            Selected but not in this workspace:{' '}
            {upstreamDatasetKeys
              .filter(k => !datasets.find(d => d.key === k))
              .join(', ')}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="source-org-id">Source Organization ID</Label>
        <p className="text-xs text-muted-foreground">
          Fetch analysis from a different organization. Leave empty to use current org.
        </p>
        <Input
          id="source-org-id"
          value={sourceOrgId}
          onChange={(e) => onChange('sourceOrgId', e.target.value || null)}
          placeholder="Current organization"
          className="max-w-xs"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="source-ws-id">Source Workspace ID</Label>
        <p className="text-xs text-muted-foreground">
          Fetch analysis from a different workspace. Leave empty to use current workspace.
        </p>
        <Input
          id="source-ws-id"
          value={sourceWsId}
          onChange={(e) => onChange('sourceWsId', e.target.value || null)}
          placeholder="Current workspace"
          className="max-w-xs"
        />
      </div>
    </div>
  )
}
